import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { IFormattedSettingValue } from 'utils';
import { TooltipIcon } from '../Tooltip';
import { Text } from '../Typography';

export interface IIntegrationSettingRowProps {
  /** Resolved setting name (or raw key for the unresolved fallback). */
  name: string;
  /** One-line description from the catalog; omitted for unresolved keys. */
  description?: string;
  /** Pre-formatted value + optional tooltip from `formatIntegrationSettingValue`. */
  value: IFormattedSettingValue;
  /** Unique id used to wire the (i) tooltip; defaults to the name. */
  tooltipId?: string;
  dataCy?: string;
}

const Row = styled.li`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${toRem(theme.space.lg)};
  padding: ${toRem(theme.space.sm)} 0;
`;

const Labels = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space['2xs'])};
  min-width: 0;
`;

const Description = styled(Text)`
  color: ${theme.color.neutral[500]};
`;

const ValueWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${toRem(theme.space.xs)};
  flex-shrink: 0;
  max-width: 45%;
`;

const ValueText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  min-width: 0;
`;

const ValueTooltipIcon = styled(TooltipIcon)`
  flex-shrink: 0;

  &.react-tooltip {
    max-width: ${toRem(260)};
    overflow-wrap: anywhere;
  }
`;

const IntegrationSettingRow = ({ name, description, value, tooltipId, dataCy = 'integration-setting-row' }: IIntegrationSettingRowProps) => {
  const id = `integration-setting-${tooltipId ?? name}`;
  const valueRef = useRef<HTMLParagraphElement>(null);
  const [truncated, setTruncated] = useState(false);

  // The displayed value is clipped with an ellipsis at a fixed max-width, so a single
  // long value (URL, id, long label) can be visually cut off with no way to read it.
  // Detect real overflow (`scrollWidth > clientWidth`) so we can reveal the full value
  // in the tooltip only when it is actually truncated — no false positives.
  useEffect(() => {
    const el = valueRef.current;
    if (!el) return undefined;

    const update = () => setTruncated(el.scrollWidth > el.clientWidth);
    update();

    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
    // `value.tooltip` toggles the (i) icon, which changes the space available to the
    // text — re-measure so the resize-listener fallback path (no ResizeObserver) stays correct.
  }, [value.display, value.tooltip]);

  // Formatter-provided tooltip (e.g. the full CSV list behind a `+N more`) takes
  // precedence; otherwise fall back to revealing the full value when it is clipped.
  const tooltipContent = value.tooltip ?? (truncated ? value.display : undefined);

  return (
    <Row data-cy={dataCy}>
      <Labels>
        <Text size="xsmall" fontWeight="medium" allowWordWrap data-cy="integration-setting-name">
          {name}
        </Text>
        {description && (
          <Description size="xxsmall" allowWordWrap data-cy="integration-setting-description">
            {description}
          </Description>
        )}
      </Labels>
      <ValueWrapper>
        {/* The (i) icon is the single tooltip trigger — the value text is intentionally
            NOT an anchor, so the same content isn't shown from two hover targets. */}
        <ValueText ref={valueRef} size="xsmall" data-cy="integration-setting-value">
          {value.display}
        </ValueText>
        {tooltipContent && (
          <ValueTooltipIcon id={id} place="left" dataCy="integration-setting-value-tooltip">
            {tooltipContent}
          </ValueTooltipIcon>
        )}
      </ValueWrapper>
    </Row>
  );
};

export default IntegrationSettingRow;
